import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Heart, Instagram, Linkedin } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-slate-900 text-white">
      <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 items-start">
          {/* Brand */}
          <div className="-ml-4">
            <Link to="/" className="inline-block -mt-10 -mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_nonprofit-redesign-1/artifacts/7zavuzbi_AimHire%20Logo.png"
                alt="AimHire Logo"
                className="w-auto brightness-0 invert"
                style={{ height: '166px' }}
              />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-3">
              Aim Hire is a nonprofit recruiting partner connecting experienced professionals 
              with organizations that value performance, reliability, and long-term contribution.
            </p>
            <div className="flex items-center text-purple-200 text-sm mb-4">
              <Heart className="w-4 h-4 mr-2" />
              <span>Employment with Dignity</span>
            </div>
            {/* Social Links */}
            <div className="flex items-center gap-3">
              <a
                href="https://www.instagram.com/aim_hireorg"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-slate-800 hover:bg-purple-600 flex items-center justify-center transition-colors duration-200"
                data-testid="footer-instagram"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="w-4 h-4 text-white" />
              </a>
              <a
                href="https://www.linkedin.com/in/aim-hire-recruiting-105708220/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-slate-800 hover:bg-purple-600 flex items-center justify-center transition-colors duration-200"
                data-testid="footer-linkedin"
                aria-label="Connect with us on LinkedIn"
              >
                <Linkedin className="w-4 h-4 text-white" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Quick Links
            </h4>
            <ul className="space-y-3">
              {[
                { name: 'Home', path: '/' },
                { name: 'For Employers', path: '/employers' },
                { name: 'For Candidates', path: '/candidates' },
                { name: 'About Us', path: '/about' },
                { name: 'Contact', path: '/contact' },
                { name: 'Donate', path: '/donate' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-white text-sm transition-colors duration-200"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start">
                <Mail className="w-5 h-5 text-purple-200 mr-3 mt-0.5 flex-shrink-0" />
                <a
                  href="mailto:info@aim-hire.org"
                  className="text-slate-400 hover:text-white text-sm transition-colors duration-200"
                  data-testid="footer-email"
                >
                  info@aim-hire.org
                </a>
              </li>
              <li className="flex items-start">
                <Phone className="w-5 h-5 text-purple-200 mr-3 mt-0.5 flex-shrink-0" />
                <a
                  href="tel:+19172460621"
                  className="text-slate-400 hover:text-white text-sm transition-colors duration-200"
                  data-testid="footer-phone"
                >
                  (917) 246-0621
                </a>
              </li>
              <li className="flex items-start">
                <MapPin className="w-5 h-5 text-purple-200 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-slate-400 text-sm">
                  1010 Broadway<br />
                  Woodmere, NY 11598
                </span>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="text-lg font-semibold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Office Hours
            </h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li className="flex justify-between">
                <span>Monday - Friday</span>
                <span>9am - 5pm</span>
              </li>
              <li className="flex justify-between">
                <span>Saturday - Sunday</span>
                <span>Closed</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-slate-500 text-sm mb-4 md:mb-0">
            © {currentYear} AimHire. All rights reserved.
          </p>
          <p className="text-slate-500 text-sm">
            A 501(c)(3) nonprofit organization
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
